'use client'

import { useState } from 'react'

import { cx } from 'class-variance-authority'
import { useTranslations } from 'next-intl'
import { ReviewStatus } from '@skop-app/lib-types'

import type { ReviewDrawerThread } from '@/types'

import { Divider } from '..'
import { MessageInput } from '../Messages/Input'
import { MessagesList } from '../Messages/List'
import { ReviewStatusBadge } from '../StatusBadge'
import { hasReviewMessages, isReviewUiVisible } from '@/utils/reviews'

type ReviewCardProps = {
  thread: ReviewDrawerThread
  isSelected?: boolean
  onSelect?: () => void
}

export function ReviewCard({
  thread,
  isSelected = false,
  onSelect,
}: ReviewCardProps) {
  const t = useTranslations()
  const [isEditing, setIsEditing] = useState(false)

  const review = thread.review
  const { status } = review
  const isClosedReview = Boolean(status && status !== ReviewStatus.PENDING)

  const shouldRenderReviewBody = isReviewUiVisible(review)
  const shouldShowInlineComposer =
    hasReviewMessages(review) || status !== ReviewStatus.PENDING

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleEdit = () => {
    onSelect?.()
    setIsEditing(true)
  }

  return (
    <div
      className={cx(
        'flex h-min w-full flex-col items-start bg-neutral-100/50 px-4 pb-4',
        isSelected && 'bg-neutral-100',
      )}
    >
      {shouldRenderReviewBody && (
        <>
          <Divider />
          <MessagesList
            review={review}
            onAddComment={shouldShowInlineComposer ? undefined : handleEdit}
          />
        </>
      )}

      {isClosedReview && (
        <div className="flex flex-wrap items-center gap-2 py-2">
          <ReviewStatusBadge status={status} />
          <span className="text-xs text-slate-400">
            {t('Review.statusLabel')}
          </span>
        </div>
      )}

      {isEditing ? (
        <div className="w-full pt-2">
          <MessageInput review={review} onCancel={handleCancel} />
        </div>
      ) : shouldShowInlineComposer ? (
        <button
          type="button"
          onClick={handleEdit}
          className="pb-4 pt-2 text-sm italic text-gray-400 transition hover:text-gray-500"
        >
          {isClosedReview ? t('Review.reopen') : t('Review.addComment')}
        </button>
      ) : null}
    </div>
  )
}
